import _ from "lodash";
import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Logo from "../../../utils/Logo";
import { toUrlName, fetchPdfBlob, downloadPdf } from "./utils";

const PDFViewer = ({ blobUrl }) => {
  if (!blobUrl) return null;

  const viewerUrl = `${blobUrl}#toolbar=0&navpanes=0&view=FitH`;

  return (
    <Box
      sx={{
        width: "100%",
        height: "70vh",
        borderRadius: 2,
        overflow: "hidden",
        mb: 2,
        bgcolor: "#525659",
      }}
    >
      <object
        data={viewerUrl}
        type="application/pdf"
        width="100%"
        height="100%"
        style={{ border: "none" }}
        aria-label="Visualizador de PDF de prescripción"
      >
        <Typography textAlign="center" color="white" p={2}>
          No se puede mostrar el PDF.
        </Typography>
      </object>
    </Box>
  );
};

const usePdfBlob = (url, shouldLoad) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!shouldLoad || !url || blobUrl) return;

    setLoading(true);
    setError(false);
    
    fetchPdfBlob(url)
      .then((blob) => {
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch((err) => {
        console.error("Error loading PDF:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [url, shouldLoad, blobUrl]);

  return { blobUrl, loading, error };
};

const PrescriptionItem = ({
  url,
  id,
  isExpanded,
  isViewing,
  onExpand,
  onView,
  onDownload,
  disableCollapse,
}) => {
  const { blobUrl, loading, error } = usePdfBlob(url, isViewing);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownload = () => {
    setIsDownloading(true);
    onDownload(url, id).finally(() => setIsDownloading(false));
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => onExpand(id)}
      sx={{
        mb: 1,
        "&:before": { display: "none" },
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderRadius: 2,
        overflow: "hidden",
        "&:first-of-type": { borderRadius: 2 },
        "&:last-of-type": { borderRadius: 2 },
      }}
    >
      <AccordionSummary
        expandIcon={disableCollapse ? null : <ExpandMoreIcon sx={{ color: isExpanded ? "white" : "primary.main" }} />}
        sx={{
          backgroundColor: isExpanded ? "primary.main" : "#f8f9fa",
          borderRadius: "inherit",
          "&:hover": { 
            backgroundColor: isExpanded ? "primary.dark" : "primary.light",
          },
          ...(disableCollapse && {
            cursor: "default",
          }),
        }}
      >
        <Typography 
          fontWeight={600}
          color={isExpanded ? "white" : "primary.dark"}
        >
          Prescripción {id}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              color: "error.main",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography color="error" textAlign="center">
              No se pudo cargar la prescripción
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Verificá tu conexión e intentá nuevamente
            </Typography>
          </Box>
        )}

        {isViewing && blobUrl && !error && (
          <>
            <PDFViewer blobUrl={blobUrl} />
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              loading={isDownloading}
            >
              Descargar
            </LoadingButton>
          </>
        )}

        {!isViewing && !loading && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              sx={{ flex: 1 }}
              startIcon={<VisibilityIcon />}
              onClick={() => onView(id)}
            >
              Ver
            </Button>
            <LoadingButton
              variant="outlined"
              color="primary"
              sx={{ flex: 1 }}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              loading={isDownloading}
            >
              Descargar
            </LoadingButton>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const PrescriptionList = ({
  urls,
  expandedId,
  viewingIds,
  onExpand,
  onView,
  onDownload,
}) => {
  const isSingle = _.size(urls) === 1;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      {!isSingle && (
        <Typography variant="subtitle1" textAlign="center" sx={{ mb: 2 }}>
          {_.size(urls)} prescripciones disponibles
        </Typography>
      )}

      {_.map(urls, (url, index) => {
        const id = toUrlName(url) || index;
        return (
          <PrescriptionItem
            key={url}
            url={url}
            id={id}
            isExpanded={isSingle || expandedId === id}
            isViewing={viewingIds.has(id)}
            onExpand={isSingle ? () => {} : onExpand}
            onView={onView}
            onDownload={onDownload}
            disableCollapse={isSingle}
          />
        );
      })}
    </Box>
  );
};

const ViewerDownloader = ({ urls }) => {
  const hasUrls = Array.isArray(urls) && urls.length > 0;
  const firstId = hasUrls ? toUrlName(urls[0]) || 0 : 0;

  const [expandedId, setExpandedId] = useState(firstId);
  const [viewingIds, setViewingIds] = useState(() => new Set([firstId]));

  const userAgent = navigator?.userAgent || "";
  const isWhatsAppiOsBrowser = /\[WAiOS\//.test(userAgent);

  const handleExpand = useCallback(
    (id) => {
      setExpandedId(expandedId === id ? null : id);
    },
    [expandedId]
  );

  const handleView = useCallback((id) => {
    setViewingIds((prev) => new Set(prev).add(id));
  }, []);

  const handleDownload = useCallback((url, id) => {
    return downloadPdf(url, id);
  }, []);

  return (
    <Stack>
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Logo width={"80px"} />
      </Box>

      <PrescriptionList
        urls={urls}
        expandedId={expandedId}
        viewingIds={viewingIds}
        onExpand={handleExpand}
        onView={handleView}
        onDownload={handleDownload}
      />

      {isWhatsAppiOsBrowser && (
        <Typography variant="body2" textAlign="center" p={2} color="text.secondary">
          Si tenés problemas para ver el PDF, hacé click en los 3 puntitos de
          arriba a la derecha y seleccioná "Abrir en el navegador".
        </Typography>
      )}
    </Stack>
  );
};

export default ViewerDownloader;
